import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { adminAPI, bookingApprovalAPI } from '../../services/api';
import { formatGuestBreakdown } from '../../utils/formatGuests';
import {
  ArrowLeft, Loader2, MapPin, Phone, Mail, User, Building2,
  BedDouble, CalendarDays, ClipboardList, Info, CheckCircle2, Lock,
  ShieldCheck, Ban, Clock, Search, SlidersHorizontal, X, FileImage
} from 'lucide-react';
import { STATUS_BADGE } from './AdminDashboard';
import GuestDocumentCard from '../../components/GuestDocumentCard';

const TABS = [
  { id: 'overview', label: 'Details & Owner', icon: Info },
  { id: 'bookings', label: 'Bookings', icon: BedDouble },
  { id: 'calendar', label: 'Calendar', icon: CalendarDays },
  { id: 'onboarding', label: 'Onboarding', icon: ClipboardList },
];

const fmtStatus = (s) => (s || 'not_started').replace('_', ' ');

const Row = ({ label, value }) => (
  <div className="flex justify-between gap-4 py-2 border-b border-slate-200 last:border-0">
    <span className="text-xs text-slate-500">{label}</span>
    <span className="text-sm text-slate-700 text-right">{value || '—'}</span>
  </div>
);

const AdminPropertyDetail = () => {
  const { propertyId } = useParams();
  const navigate = useNavigate();
  const [tab, setTab] = useState('overview');
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);

  // tab data
  const [bookings, setBookings] = useState(null);
  const [calendar, setCalendar] = useState(null);
  const [onboarding, setOnboarding] = useState(null);
  const [tabLoading, setTabLoading] = useState(false);

  // iter92 — Sneha approval state (per-booking submission flag + result toast)
  const [snehaPending, setSnehaPending] = useState(null);     // booking_id currently submitting
  const [snehaError, setSnehaError] = useState(null);         // {booking_id, message}

  // iter97 — Booking search + filter (client-side over already-loaded data)
  const [searchInput, setSearchInput] = useState('');         // raw user input
  const [searchQuery, setSearchQuery] = useState('');         // debounced
  const [filterOpen, setFilterOpen] = useState(false);
  const [draftFilters, setDraftFilters] = useState({          // edited in panel, not yet applied
    checkInFrom: '', checkInTo: '', bookingDateFrom: '', bookingDateTo: '',
  });
  const [appliedFilters, setAppliedFilters] = useState({
    checkInFrom: '', checkInTo: '', bookingDateFrom: '', bookingDateTo: '',
  });
  const debounceRef = useRef(null);

  // Debounce the search input so we don't re-filter on every keystroke when list is large
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setSearchQuery(searchInput.trim()), 150);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [searchInput]);

  const hasActiveFilters = useMemo(() => (
    Object.values(appliedFilters).some(Boolean)
  ), [appliedFilters]);

  const filteredBookings = useMemo(() => {
    const rows = bookings?.bookings || [];
    if (!rows.length) return rows;
    const q = searchQuery.toLowerCase();
    const inRange = (val, from, to) => {
      if (!val) return !from && !to;            // missing date is only included when no range chosen
      const d = String(val).slice(0, 10);       // works for both "YYYY-MM-DD" and ISO datetimes
      if (from && d < from) return false;
      if (to && d > to) return false;
      return true;
    };
    return rows.filter((b) => {
      if (q && !String(b.booking_id || '').toLowerCase().includes(q)) return false;
      if (!inRange(b.check_in, appliedFilters.checkInFrom, appliedFilters.checkInTo)) return false;
      if (!inRange(b.booking_date || b.created_at, appliedFilters.bookingDateFrom, appliedFilters.bookingDateTo)) return false;
      return true;
    });
  }, [bookings, searchQuery, appliedFilters]);

  const applyFilters = () => { setAppliedFilters(draftFilters); setFilterOpen(false); };
  const resetFilters = () => {
    const empty = { checkInFrom: '', checkInTo: '', bookingDateFrom: '', bookingDateTo: '' };
    setDraftFilters(empty); setAppliedFilters(empty); setSearchInput(''); setSearchQuery('');
  };

  const submitSnehaApprove = async (bookingId) => {
    if (!bookingId || snehaPending) return;
    setSnehaPending(bookingId); setSnehaError(null);
    try {
      const res = await bookingApprovalAPI.snehaApprove(bookingId);
      if (res?.STATUS === 1 || res?.success || res?.booking) {
        // Optimistic: flip sneha_status + approval_locked locally
        setBookings((prev) => prev && ({
          ...prev,
          bookings: (prev.bookings || []).map((b) =>
            b.booking_id === bookingId
              ? { ...b, sneha_status: 'Approved', approval_locked: true, sneha_updated_by: res?.RESULT?.sneha_updated_by || res?.booking?.sneha_updated_by || 'Sneha (BFR)' }
              : b
          ),
        }));
      } else {
        setSnehaError({ booking_id: bookingId, message: res?.MESSAGE || 'Failed to approve.' });
      }
    } catch (err) {
      setSnehaError({ booking_id: bookingId, message: err?._friendlyMessage || err?.detail || 'Failed to approve.' });
    } finally {
      setSnehaPending(null);
    }
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const res = await adminAPI.getProperty(propertyId);
        setDetail(res?.STATUS === 1 ? res.RESULT : null);
      } finally {
        setLoading(false);
      }
    })();
  }, [propertyId]);

  const monthRange = () => {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0, 10);
    const end = new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().slice(0, 10);
    return { start, end };
  };

  const loadTab = useCallback(async (t) => {
    if (t === 'bookings' && bookings === null) {
      setTabLoading(true);
      try { const r = await adminAPI.getBookings(propertyId, 100, 0); setBookings(r?.RESULT || { total: 0, bookings: [] }); }
      finally { setTabLoading(false); }
    } else if (t === 'calendar' && calendar === null) {
      setTabLoading(true);
      const { start, end } = monthRange();
      try { const r = await adminAPI.getCalendar(propertyId, start, end); setCalendar(r?.RESULT || { bookings: [], start, end }); }
      finally { setTabLoading(false); }
    } else if (t === 'onboarding' && onboarding === null) {
      setTabLoading(true);
      try { const r = await adminAPI.getOnboarding(propertyId); setOnboarding(r?.RESULT || null); }
      finally { setTabLoading(false); }
    }
  }, [propertyId, bookings, calendar, onboarding]);

  useEffect(() => { loadTab(tab); }, [tab, loadTab]);

  if (loading) {
    return <div className="min-h-screen bg-white flex items-center justify-center"><Loader2 className="w-7 h-7 text-[#1B2A7B] animate-spin" /></div>;
  }
  if (!detail) {
    return <div className="min-h-screen bg-white flex flex-col items-center justify-center text-slate-500 gap-3">
      <p>Property not found.</p>
      <button onClick={() => navigate('/admin')} className="text-[#1B2A7B]">Back to dashboard</button>
    </div>;
  }

  return (
    <div className="min-h-screen bg-white pb-28" data-testid="admin-property-detail">
      {/* Header — navy brand strip. iter159 redesign. */}
      <div className="bg-[#1B2A7B] border-b border-[#152163] sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-3">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/admin')} className="p-1.5 rounded-lg hover:bg-white/10 text-white/80 hover:text-white" data-testid="admin-detail-back">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="w-10 h-10 rounded-lg bg-white/15 border border-white/25 flex items-center justify-center">
              <Building2 className="w-5 h-5 text-white" />
            </div>
            <div className="min-w-0">
              <h1 className="text-white font-bold text-base truncate" data-testid="admin-detail-name">{detail.property_name}</h1>
              <p className="text-[11px] text-white/70">{detail.grb_id || '—'} · {detail.city || '—'}</p>
            </div>
          </div>
          {/* Tabs */}
          <div className="flex gap-1 mt-3 overflow-x-auto">
            {TABS.map((t) => {
              const Icon = t.icon;
              const active = tab === t.id;
              return (
                <button
                  key={t.id}
                  onClick={() => setTab(t.id)}
                  data-testid={`admin-tab-${t.id}`}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-colors ${active ? 'bg-white text-[#1B2A7B]' : 'text-white/80 hover:bg-white/10'}`}
                >
                  <Icon className="w-3.5 h-3.5" /> {t.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-5">
        {/* OVERVIEW */}
        {tab === 'overview' && (
          <div className="space-y-4" data-testid="admin-tab-content-overview">
            <div className="bg-white border border-slate-200 rounded-xl p-4">
              <h2 className="text-[#1B2A7B] font-semibold text-sm mb-3 flex items-center gap-2"><Info className="w-4 h-4 text-[#1B2A7B]" /> Property Details</h2>
              <Row label="Property Name" value={detail.property_name} />
              <Row label="GRB ID" value={detail.grb_id} />
              <Row label="City / State" value={[detail.city, detail.state].filter(Boolean).join(', ')} />
              <Row label="Address" value={detail.address} />
              <Row label="Onboarding Status" value={fmtStatus(detail.onboarding_status)} />
              <Row label="Total Bookings" value={String(detail.booking_count ?? 0)} />
            </div>

            <div className="bg-white border border-slate-200 rounded-xl p-4">
              <h2 className="text-[#1B2A7B] font-semibold text-sm mb-3 flex items-center gap-2"><User className="w-4 h-4 text-[#1B2A7B]" /> Owner Information</h2>
              {(detail.owners || []).length === 0 ? (
                <p className="text-sm text-slate-500">No linked owners.</p>
              ) : (
                <div className="space-y-3">
                  {detail.owners.map((o, i) => (
                    <div key={i} className="flex items-center gap-3 bg-slate-100/50 rounded-lg p-3" data-testid={`admin-owner-${i}`}>
                      <div className="w-9 h-9 rounded-full bg-slate-700 flex items-center justify-center text-slate-600 text-sm font-semibold">
                        {(o.name || '?').charAt(0).toUpperCase()}
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm text-slate-800 truncate">{o.name || '—'}</p>
                        <div className="flex items-center gap-3 text-[11px] text-slate-500 mt-0.5">
                          {o.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{o.phone}</span>}
                          <span className="px-1.5 py-0.5 rounded bg-slate-700 text-slate-600">{o.role || 'owner'}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {detail.email && (
                <p className="text-xs text-slate-500 mt-3 flex items-center gap-1.5"><Mail className="w-3.5 h-3.5" />{detail.email}</p>
              )}
            </div>
          </div>
        )}

        {/* BOOKINGS */}
        {tab === 'bookings' && (
          <div data-testid="admin-tab-content-bookings">
            {tabLoading ? <Spinner /> : (
              <>
                {/* iter97 — Search + Filter toolbar */}
                <div className="mb-3">
                  <div className="flex items-center gap-2">
                    <div className="flex-1 relative">
                      <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                      <input
                        type="text"
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.target.value)}
                        placeholder="Search by Booking ID"
                        data-testid="booking-search-input"
                        className="w-full pl-9 pr-9 py-2 text-sm bg-white border border-slate-200 rounded-lg text-slate-900 placeholder-slate-500 focus:border-[#1B2A7B] focus:outline-none"
                      />
                      {searchInput && (
                        <button
                          type="button"
                          onClick={() => setSearchInput('')}
                          data-testid="booking-search-clear"
                          className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded text-slate-500 hover:text-slate-600 hover:bg-slate-100"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => { setDraftFilters(appliedFilters); setFilterOpen((v) => !v); }}
                      data-testid="booking-filter-toggle"
                      className={`flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg border transition ${
                        hasActiveFilters
                          ? 'bg-[#1B2A7B] border-[#1B2A7B] text-white font-semibold'
                          : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-100'
                      }`}
                    >
                      <SlidersHorizontal className="w-4 h-4" />
                      Filter
                      {hasActiveFilters && (
                        <span className="ml-1 text-[10px] bg-white/20 px-1.5 py-0.5 rounded-full" data-testid="booking-filter-active-count">
                          {Object.values(appliedFilters).filter(Boolean).length}
                        </span>
                      )}
                    </button>
                  </div>

                  {/* Filter panel */}
                  {filterOpen && (
                    <div className="mt-2 p-3 bg-white border border-slate-200 rounded-lg" data-testid="booking-filter-panel">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                          <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1.5 font-semibold">Check-In Date</p>
                          <div className="flex items-center gap-2">
                            <input
                              type="date"
                              value={draftFilters.checkInFrom}
                              onChange={(e) => setDraftFilters((d) => ({ ...d, checkInFrom: e.target.value }))}
                              data-testid="filter-checkin-from"
                              className="flex-1 px-2 py-1.5 text-xs bg-slate-50 border border-slate-200 rounded text-slate-900 focus:border-[#1B2A7B] focus:outline-none"
                            />
                            <span className="text-slate-500 text-xs">→</span>
                            <input
                              type="date"
                              value={draftFilters.checkInTo}
                              onChange={(e) => setDraftFilters((d) => ({ ...d, checkInTo: e.target.value }))}
                              data-testid="filter-checkin-to"
                              className="flex-1 px-2 py-1.5 text-xs bg-slate-50 border border-slate-200 rounded text-slate-900 focus:border-[#1B2A7B] focus:outline-none"
                            />
                          </div>
                        </div>
                        <div>
                          <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1.5 font-semibold">Booking Date</p>
                          <div className="flex items-center gap-2">
                            <input
                              type="date"
                              value={draftFilters.bookingDateFrom}
                              onChange={(e) => setDraftFilters((d) => ({ ...d, bookingDateFrom: e.target.value }))}
                              data-testid="filter-bookingdate-from"
                              className="flex-1 px-2 py-1.5 text-xs bg-slate-50 border border-slate-200 rounded text-slate-900 focus:border-[#1B2A7B] focus:outline-none"
                            />
                            <span className="text-slate-500 text-xs">→</span>
                            <input
                              type="date"
                              value={draftFilters.bookingDateTo}
                              onChange={(e) => setDraftFilters((d) => ({ ...d, bookingDateTo: e.target.value }))}
                              data-testid="filter-bookingdate-to"
                              className="flex-1 px-2 py-1.5 text-xs bg-slate-50 border border-slate-200 rounded text-slate-900 focus:border-[#1B2A7B] focus:outline-none"
                            />
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center justify-end gap-2 mt-3">
                        <button
                          type="button"
                          onClick={resetFilters}
                          data-testid="filter-reset"
                          className="px-3 py-1.5 text-xs rounded border border-slate-200 text-slate-600 hover:bg-slate-100"
                        >
                          Reset Filters
                        </button>
                        <button
                          type="button"
                          onClick={applyFilters}
                          data-testid="filter-apply"
                          className="px-3 py-1.5 text-xs rounded bg-[#1B2A7B] text-white font-semibold hover:bg-[#1B2A7B]"
                        >
                          Apply Filters
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                <p className="text-xs text-slate-500 mb-3" data-testid="booking-result-count">
                  Showing {filteredBookings.length} of {bookings?.total ?? bookings?.bookings?.length ?? 0} bookings
                  {(searchQuery || hasActiveFilters) && (
                    <button
                      onClick={resetFilters}
                      data-testid="booking-clear-all"
                      className="ml-2 text-[#1B2A7B] hover:underline"
                    >
                      Clear all
                    </button>
                  )}
                </p>
                {filteredBookings.length === 0 ? (
                  <Empty text={(searchQuery || hasActiveFilters) ? 'No bookings match your filters.' : 'No bookings for this property.'} />
                ) : (
                  <div className="space-y-3">
                    {filteredBookings.map((b, i) => {
                      const ownerStatus = b.owner_status;        // undefined for Walk-in (serializer returns {})
                      const snehaStatus = b.sneha_status;
                      const hasApprovalFields = !!ownerStatus || !!snehaStatus;
                      const canSnehaApprove =
                        hasApprovalFields &&
                        ownerStatus === 'Rejected' &&
                        snehaStatus !== 'Approved' &&
                        !b.approval_locked;
                      const ownerBadge =
                        ownerStatus === 'Approved' ? 'bg-green-100 text-green-700 border-green-200'
                        : ownerStatus === 'Rejected' ? 'bg-red-100 text-red-300 border-red-200'
                        : 'bg-slate-100 text-slate-500 border-slate-200';
                      const snehaBadge =
                        snehaStatus === 'Approved' ? 'bg-green-100 text-green-700 border-green-200'
                        : 'bg-slate-100 text-slate-500 border-slate-200';
                      const errForThis = snehaError?.booking_id === b.booking_id ? snehaError.message : null;
                      const amount = b.grand_total || b.total_amount || 0;
                      const guests = formatGuestBreakdown(b);
                      const roomCount = b.no_of_rooms || b.room_count || (Array.isArray(b.rooms) ? b.rooms.length : (b.rooms || 1));
                      const fmtDate = (d) => {
                        if (!d) return '—';
                        // iter99 — Parse YYYY-MM-DD directly. NEVER use new Date() — it interprets
                        // bare date strings as UTC midnight which causes TZ-dependent rendering
                        // (e.g. shows previous day for US viewers). Property dates are calendar
                        // dates and must render identically across every timezone.
                        const s = String(d).slice(0, 10);
                        const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
                        if (!m) return s;
                        const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
                        return `${m[3]} ${months[+m[2] - 1]} ${m[1]}`;
                      };
                      const isApprovedByOwner = ownerStatus === 'Approved';
                      const isRejectedByOwner = ownerStatus === 'Rejected';
                      const isApprovedBySneha = snehaStatus === 'Approved';
                      return (
                      <div key={b.booking_id || i} className="bg-white border border-slate-200 rounded-xl p-4" data-testid={`admin-booking-${i}`}>
                        {/* Header — Booking ID + Booking Status */}
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <p className="text-[10px] uppercase tracking-wider text-slate-500">Booking ID</p>
                            <p className="text-sm font-mono text-slate-800 truncate" data-testid={`admin-booking-id-${b.booking_id || i}`}>{b.booking_id || '—'}</p>
                          </div>
                          <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 capitalize whitespace-nowrap">{b.booking_status || '—'}</span>
                        </div>

                        {/* Booking details grid */}
                        <div className="grid grid-cols-2 gap-x-4 gap-y-2 mt-3 text-[12px]">
                          <div>
                            <p className="text-[10px] uppercase tracking-wider text-slate-500">Guest Name</p>
                            <p className="text-slate-700 truncate">{b.guest_name || '—'}</p>
                          </div>
                          <div>
                            <p className="text-[10px] uppercase tracking-wider text-slate-500">OTA Source</p>
                            <p className="text-slate-700 truncate">{b.ota_name || '—'}</p>
                          </div>
                          <div>
                            <p className="text-[10px] uppercase tracking-wider text-slate-500">Check-In</p>
                            <p className="text-slate-700">{fmtDate(b.check_in)}</p>
                          </div>
                          <div>
                            <p className="text-[10px] uppercase tracking-wider text-slate-500">Check-Out</p>
                            <p className="text-slate-700">{fmtDate(b.check_out)}</p>
                          </div>
                          <div>
                            <p className="text-[10px] uppercase tracking-wider text-slate-500">Rooms</p>
                            <p className="text-slate-700">{roomCount}</p>
                          </div>
                          <div>
                            <p className="text-[10px] uppercase tracking-wider text-slate-500">Guests</p>
                            <p className="text-slate-700">{guests}</p>
                          </div>
                          <div className="col-span-2">
                            <p className="text-[10px] uppercase tracking-wider text-slate-500">Room Type</p>
                            <p className="text-slate-700 truncate">{b.room_type || '—'}</p>
                          </div>
                          <div>
                            <p className="text-[10px] uppercase tracking-wider text-slate-500">Booking Amount</p>
                            <p className="text-slate-800 font-semibold">₹{Number(amount).toLocaleString('en-IN')}</p>
                          </div>
                          <div>
                            <p className="text-[10px] uppercase tracking-wider text-slate-500">Property</p>
                            <p className="text-slate-700 truncate">{b.property_name || detail.property_name || '—'}</p>
                          </div>
                        </div>

                        {/* Approval workflow section (OTA only — walk-ins skip this block entirely) */}
                        {hasApprovalFields && (
                          <div className="mt-3 pt-3 border-t border-slate-200" data-testid={`admin-booking-approval-${b.booking_id || i}`}>
                            <div className="flex flex-wrap items-center gap-2">
                              <span className={`inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full border ${ownerBadge}`} data-testid={`admin-owner-status-${b.booking_id || i}`}>
                                {isApprovedByOwner ? <ShieldCheck className="w-3 h-3" />
                                  : isRejectedByOwner ? <Ban className="w-3 h-3" />
                                  : <Clock className="w-3 h-3" />}
                                Owner: {ownerStatus || 'Pending'}
                              </span>
                              <span className={`inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full border ${snehaBadge}`} data-testid={`admin-sneha-status-${b.booking_id || i}`}>
                                {isApprovedBySneha ? <ShieldCheck className="w-3 h-3" /> : <Clock className="w-3 h-3" />}
                                {snehaStatus || 'Pending'}
                              </span>
                              {b.approval_locked && (
                                <span className="inline-flex items-center gap-1 text-[10px] text-[#1B2A7B]" title="Approval state is locked">
                                  <Lock className="w-3 h-3" /> Locked
                                </span>
                              )}
                              {canSnehaApprove && (
                                <button
                                  onClick={() => submitSnehaApprove(b.booking_id)}
                                  disabled={snehaPending === b.booking_id}
                                  data-testid={`sneha-approve-btn-${b.booking_id}`}
                                  className="ml-auto inline-flex items-center gap-1 text-[11px] font-semibold px-3 py-1.5 rounded-md bg-[#1B2A7B] hover:bg-[#152163] text-white disabled:opacity-60 transition-colors"
                                >
                                  {snehaPending === b.booking_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />}
                                  Approve Booking
                                </button>
                              )}
                            </div>

                            {/* Rejection reason (Owner Rejected) */}
                            {isRejectedByOwner && (
                              <div className="mt-2 px-3 py-2 rounded-lg bg-red-950/40 border border-red-900/60" data-testid={`admin-rejection-reason-${b.booking_id || i}`}>
                                <p className="text-[10px] uppercase tracking-wider text-red-400">Rejection Reason</p>
                                <p className="text-[12px] text-red-700 mt-0.5">
                                  {b.rejection_reason || '—'}
                                  {b.rejection_reason === 'Other' && b.other_rejection_reason ? ` — ${b.other_rejection_reason}` : ''}
                                </p>
                                {b.rejected_by && (
                                  <p className="text-[10px] text-red-400/80 mt-1">
                                    by {b.rejected_by}{b.rejected_at ? ` · ${new Date(b.rejected_at).toLocaleString('en-IN')}` : ''}
                                  </p>
                                )}
                              </div>
                            )}

                            {/* Final-state message banners */}
                            {isApprovedByOwner && (
                              <p className="mt-2 text-[12px] text-green-700 inline-flex items-center gap-1.5" data-testid={`admin-final-message-${b.booking_id || i}`}>
                                <ShieldCheck className="w-3.5 h-3.5" /> Booking approved by owner
                              </p>
                            )}
                            {isRejectedByOwner && isApprovedBySneha && (
                              <p className="mt-2 text-[12px] text-green-700 inline-flex items-center gap-1.5" data-testid={`admin-final-message-${b.booking_id || i}`}>
                                <ShieldCheck className="w-3.5 h-3.5" /> Booking approved after BFR review
                                {b.sneha_updated_by ? ` · by ${b.sneha_updated_by}` : ''}
                                {b.sneha_updated_at ? ` · ${new Date(b.sneha_updated_at).toLocaleString('en-IN')}` : ''}
                              </p>
                            )}
                          </div>
                        )}
                        {errForThis && (
                          <p className="mt-1.5 text-[11px] text-red-400" data-testid={`sneha-approve-error-${b.booking_id}`}>{errForThis}</p>
                        )}

                        {/* iter106 — Guest Aadhaar preview (read-only for BFR) */}
                        {b.has_guest_document && (
                          <div className="mt-3" data-testid={`admin-booking-guest-doc-${b.booking_id || i}`}>
                            <GuestDocumentCard bookingId={b.booking_id} readOnly />
                          </div>
                        )}
                        {!b.has_guest_document && (
                          <div className="mt-3 px-3 py-2 rounded-lg bg-white/60 border border-slate-200 text-[11px] text-slate-500 inline-flex items-center gap-1.5" data-testid={`admin-booking-no-guest-doc-${b.booking_id || i}`}>
                            <FileImage className="w-3 h-3" /> No guest document on file
                          </div>
                        )}

                        {/* iter106 — Check-in decision (read-only) */}
                        {b.checkin_decision && (
                          <div className="mt-2 inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full border" data-testid={`admin-checkin-decision-${b.booking_id || i}`}
                            style={{
                              borderColor: b.checkin_decision === 'confirmed' ? '#065F46' : '#7F1D1D',
                              color: b.checkin_decision === 'confirmed' ? '#34D399' : '#F87171',
                              background: b.checkin_decision === 'confirmed' ? 'rgba(6,95,70,0.2)' : 'rgba(127,29,29,0.2)',
                            }}
                          >
                            {b.checkin_decision === 'confirmed' ? '✓ Checked In' : '✗ No Show'}
                            {b.checkin_decision_at ? ` · ${new Date(b.checkin_decision_at).toLocaleString('en-IN')}` : ''}
                          </div>
                        )}
                      </div>
                      );
                    })}
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* CALENDAR */}
        {tab === 'calendar' && (
          <div data-testid="admin-tab-content-calendar">
            {tabLoading ? <Spinner /> : (
              <>
                <p className="text-xs text-slate-500 mb-3">This month: {calendar?.start} → {calendar?.end} · {(calendar?.bookings || []).length} bookings</p>
                {(calendar?.bookings || []).length === 0 ? (
                  <Empty text="No bookings this month." />
                ) : (
                  <div className="space-y-2">
                    {calendar.bookings.map((b, i) => (
                      <div key={b.booking_id || i} className="bg-white border border-slate-200 rounded-xl p-3 flex items-center gap-3" data-testid={`admin-calendar-booking-${i}`}>
                        <div className="text-center bg-slate-100 rounded-lg px-2.5 py-1.5">
                          <p className="text-[10px] text-slate-500 leading-none">{(b.check_in || '').slice(5)}</p>
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm text-slate-800 truncate">{b.guest_name || 'Guest'}</p>
                          <p className="text-[11px] text-slate-500">{b.nights || '?'} night(s) · {b.booking_status}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* ONBOARDING */}
        {tab === 'onboarding' && (
          <div data-testid="admin-tab-content-onboarding">
            {tabLoading ? <Spinner /> : !onboarding ? <Empty text="No onboarding data." /> : (
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <span className={`text-xs px-2.5 py-1 rounded-full border ${STATUS_BADGE[onboarding.status] || STATUS_BADGE.not_started}`} data-testid="admin-onboarding-status">
                    {onboarding.status === 'completed' && <CheckCircle2 className="w-3 h-3 inline mr-1" />}
                    {fmtStatus(onboarding.status)}
                  </span>
                </div>

                <Section title="Property">
                  {Object.entries(onboarding.property_details || {}).map(([k, v]) => <Row key={k} label={prettify(k)} value={fmtVal(v)} />)}
                </Section>
                <Section title="Location">
                  {Object.entries(onboarding.location_details || {}).map(([k, v]) => <Row key={k} label={prettify(k)} value={fmtVal(v)} />)}
                </Section>

                <Section title={`Room Inventory (${(onboarding.room_inventory || []).length})`}>
                  {(onboarding.room_inventory || []).length === 0 ? <p className="text-sm text-slate-500">None</p> :
                    onboarding.room_inventory.map((r, i) => (
                      <div key={i} className="py-2 border-b border-slate-200 last:border-0">
                        <p className="text-sm text-slate-700">{r.room_type} {r.ac_type ? `(${r.ac_type})` : ''}</p>
                        <p className="text-[11px] text-slate-500">{r.no_of_rooms || '?'} rooms · ₹{r.base_price ?? '—'}/night</p>
                      </div>
                    ))}
                </Section>

                <Section title="Amenities">
                  <Row label="Room Amenities" value={(onboarding.amenities?.room_amenities || []).join(', ')} />
                  <Row label="Policies" value={(onboarding.amenities?.property_policies || []).join(', ')} />
                  <Row label="Chargeable" value={(onboarding.amenities?.chargeable_facilities || []).join(', ')} />
                  <Row label="Other" value={(onboarding.amenities?.other_facilities || []).join(', ')} />
                  {Object.keys(onboarding.amenities?.amenity_options || {}).length > 0 && (
                    <div className="pt-2">
                      <p className="text-xs text-slate-500 mb-1">Conditional options</p>
                      {Object.entries(onboarding.amenities.amenity_options).map(([amen, opts]) => (
                        <p key={amen} className="text-[11px] text-slate-600" data-testid={`admin-amenity-option-${amen.replace(/\s+/g, '-').toLowerCase()}`}>
                          <span className="text-[#1B2A7B]">{amen}</span>: {Object.entries(opts).map(([k, v]) => `${prettify(k)}=${v}`).join(', ')}
                        </p>
                      ))}
                    </div>
                  )}
                </Section>

                <Section title="Banking">
                  {Object.entries(onboarding.banking_details || {}).filter(([k]) => !k.endsWith('_url')).map(([k, v]) => <Row key={k} label={prettify(k)} value={fmtVal(v)} />)}
                  <div className="flex gap-4 pt-2">
                    {onboarding.banking_details?.payment_qr_url && <img src={onboarding.banking_details.payment_qr_url} alt="Payment QR" className="h-24 rounded-lg border border-slate-200" data-testid="admin-onboarding-qr" />}
                    {onboarding.banking_details?.cancelled_cheque_url && <img src={onboarding.banking_details.cancelled_cheque_url} alt="Cheque" className="h-24 rounded-lg border border-slate-200" data-testid="admin-onboarding-cheque" />}
                  </div>
                </Section>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const Spinner = () => <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 text-[#1B2A7B] animate-spin" /></div>;
const Empty = ({ text }) => <div className="text-center py-16 text-slate-500 text-sm">{text}</div>;
const Section = ({ title, children }) => (
  <div className="bg-white border border-slate-200 rounded-xl p-4">
    <h3 className="text-[#1B2A7B] font-semibold text-sm mb-2">{title}</h3>
    {children}
  </div>
);
const prettify = (k) => k.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
const fmtVal = (v) => (v === null || v === undefined || v === '' ? '—' : (typeof v === 'boolean' ? (v ? 'Yes' : 'No') : String(v)));

export default AdminPropertyDetail;
